/**
 * Canonical page descriptor registry.
 *
 * P2-010: Every generic route references a registered PageDescriptor.
 * Do not fabricate an empty page at runtime.
 */
import { ROUTE_IDS, MODULE_KEYS, FEATURE_KEYS, TELEMETRY_IDS, I18N_KEYS } from '../routes/ids.js';
const baseStates = {
    loading: {
        type: 'LOADING',
        titleKey: I18N_KEYS.COMMON_LOADING,
        messageKey: I18N_KEYS.COMMON_LOADING,
    },
    empty: { type: 'EMPTY', titleKey: I18N_KEYS.COMMON_EMPTY, messageKey: I18N_KEYS.COMMON_EMPTY },
    error: {
        type: 'ERROR',
        titleKey: I18N_KEYS.COMMON_ERROR,
        messageKey: I18N_KEYS.COMMON_ERROR,
        retryable: true,
    },
    forbidden: {
        type: 'FORBIDDEN',
        titleKey: I18N_KEYS.COMMON_FORBIDDEN,
        messageKey: I18N_KEYS.COMMON_FORBIDDEN,
    },
    notFound: {
        type: 'NOT_FOUND',
        titleKey: I18N_KEYS.COMMON_NOT_FOUND,
        messageKey: I18N_KEYS.COMMON_NOT_FOUND,
    },
    degraded: {
        type: 'DEGRADED',
        titleKey: I18N_KEYS.COMMON_DEGRADED,
        messageKey: I18N_KEYS.COMMON_DEGRADED,
    },
    stale: { type: 'STALE', titleKey: I18N_KEYS.COMMON_STALE, messageKey: I18N_KEYS.COMMON_STALE },
};
export const canonicalPageRegistry = [
    {
        id: 'page-login',
        routeId: ROUTE_IDS.LOGIN,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_LOGIN,
        telemetryId: TELEMETRY_IDS.AUTH_LOGIN,
        accessibilityLabelKey: I18N_KEYS.AUTH_LOGIN,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_LOGIN,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-mfa-verify',
        routeId: ROUTE_IDS.MFA_VERIFY,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_VERIFY,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_VERIFY,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_VERIFY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_MFA_VERIFY,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-initial-password-change',
        routeId: ROUTE_IDS.INITIAL_PASSWORD_CHANGE,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_VERIFY,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_VERIFY,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_VERIFY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_MFA_VERIFY,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-mfa-enroll',
        routeId: ROUTE_IDS.MFA_ENROLL,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_ENROLL,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_ENROLL,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_ENROLL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_MFA_ENROLL,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-mfa-step-up',
        routeId: ROUTE_IDS.MFA_STEP_UP,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_MFA_ENROLL,
        telemetryId: TELEMETRY_IDS.AUTH_MFA_ENROLL,
        accessibilityLabelKey: I18N_KEYS.AUTH_MFA_ENROLL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_MFA_ENROLL,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-dashboard',
        routeId: ROUTE_IDS.DASHBOARD,
        schemaVersion: 1,
        module: MODULE_KEYS.DASHBOARD,
        feature: FEATURE_KEYS.DASHBOARD_VIEW,
        telemetryId: TELEMETRY_IDS.DASHBOARD_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_DASHBOARD,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.NAVIGATION_DASHBOARD,
        layout: 'dashboard',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-transactions',
        routeId: ROUTE_IDS.TRANSACTIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_LIST,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_LIST,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_TRANSACTIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.NAVIGATION_TRANSACTIONS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-transaction-form',
        routeId: ROUTE_IDS.TRANSACTION_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_CREATE,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_CREATE,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_CREATE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.TRANSACTION_CREATE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-transaction-detail',
        routeId: ROUTE_IDS.TRANSACTION_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_DETAIL,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_DETAIL,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_DETAIL,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.TRANSACTION_DETAIL,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-transaction-approvals',
        routeId: ROUTE_IDS.TRANSACTION_APPROVALS,
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_APPROVALS,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_APPROVALS,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_APPROVALS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.TRANSACTION_APPROVALS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-transaction-reversals',
        routeId: ROUTE_IDS.TRANSACTION_REVERSALS,
        schemaVersion: 1,
        module: MODULE_KEYS.TRANSACTIONS,
        feature: FEATURE_KEYS.TRANSACTIONS_REVERSALS,
        telemetryId: TELEMETRY_IDS.TRANSACTIONS_REVERSALS,
        accessibilityLabelKey: I18N_KEYS.TRANSACTION_REVERSALS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.TRANSACTION_REVERSALS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings',
        routeId: ROUTE_IDS.SETTINGS,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_VIEW,
        telemetryId: TELEMETRY_IDS.SETTINGS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_SETTINGS,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.NAVIGATION_SETTINGS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings-profile',
        routeId: ROUTE_IDS.SETTINGS_PROFILE,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PROFILE,
        telemetryId: TELEMETRY_IDS.SETTINGS_PROFILE,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PROFILE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_PROFILE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings-preferences',
        routeId: ROUTE_IDS.SETTINGS_PREFERENCES,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PREFERENCES,
        telemetryId: TELEMETRY_IDS.SETTINGS_PREFERENCES,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PREFERENCES,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_PREFERENCES,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings-mfa',
        routeId: ROUTE_IDS.SETTINGS_MFA,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_MFA,
        telemetryId: TELEMETRY_IDS.SETTINGS_MFA,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_MFA,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_MFA,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings-password',
        routeId: ROUTE_IDS.SETTINGS_PASSWORD,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_PASSWORD,
        telemetryId: TELEMETRY_IDS.SETTINGS_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_PASSWORD,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_PASSWORD,
        layout: 'single',
        states: baseStates,
        sections: [
            {
                id: 'section-settings-password-form',
                schemaVersion: 1,
                sectionType: 'FORM',
                module: MODULE_KEYS.SETTINGS,
                feature: FEATURE_KEYS.SETTINGS_PASSWORD,
                telemetryId: TELEMETRY_IDS.SETTINGS_PASSWORD,
                accessibilityLabelKey: I18N_KEYS.SETTINGS_PASSWORD,
                privacyClassification: 'CONFIDENTIAL',
                owner: 'platform',
                availability: 'AVAILABLE',
            },
        ],
    },
    {
        id: 'page-settings-sessions',
        routeId: ROUTE_IDS.SETTINGS_SESSIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_SESSIONS,
        telemetryId: TELEMETRY_IDS.SETTINGS_SESSIONS,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_SESSIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_SESSIONS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-settings-security',
        routeId: ROUTE_IDS.SETTINGS_SECURITY,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTINGS,
        feature: FEATURE_KEYS.SETTINGS_SECURITY,
        telemetryId: TELEMETRY_IDS.SETTINGS_SECURITY,
        accessibilityLabelKey: I18N_KEYS.SETTINGS_SECURITY,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'SUCCESS_ONLY',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.SETTINGS_SECURITY,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Admin hub sub-pages
    {
        id: 'page-users',
        routeId: ROUTE_IDS.USERS,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-roles',
        routeId: ROUTE_IDS.ROLES,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        titleKey: I18N_KEYS.PAGE_ROLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-profile',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-profile-detail',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_DETAIL_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_DETAIL_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-profile-edit',
        routeId: ROUTE_IDS.ORGANIZATION_PROFILE_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-audit-explorer',
        routeId: ROUTE_IDS.AUDIT_EXPLORER,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_AUDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-access-inspector',
        routeId: ROUTE_IDS.PLATFORM_ACCESS_INSPECTOR,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PLATFORM_ACCESS_INSPECTOR,
        telemetryId: TELEMETRY_IDS.ADMIN_PLATFORM_ACCESS_INSPECTOR,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ACCESS_INSPECTOR_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-access-inspector',
        routeId: ROUTE_IDS.ORGANIZATION_ACCESS_INSPECTOR,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_ACCESS_INSPECTOR,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_ACCESS_INSPECTOR,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ACCESS_INSPECTOR_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-users-create',
        routeId: ROUTE_IDS.USERS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_CREATE,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_CREATE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_USERS_CREATE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-users-detail',
        routeId: ROUTE_IDS.USERS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-users-edit',
        routeId: ROUTE_IDS.USERS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS_EDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_USERS_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_USERS_EDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-roles-create',
        routeId: ROUTE_IDS.ROLES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_CREATE,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_CREATE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ROLES_CREATE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-roles-detail',
        routeId: ROUTE_IDS.ROLES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_DETAIL,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ROLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-roles-edit',
        routeId: ROUTE_IDS.ROLES_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES_EDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ROLES_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ROLES_EDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Planned pages for routes that are not yet fully implemented
    {
        id: 'page-holdings',
        routeId: ROUTE_IDS.HOLDINGS,
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.HOLDINGS_VIEW,
        telemetryId: TELEMETRY_IDS.HOLDINGS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_HOLDINGS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.NAVIGATION_HOLDINGS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-reports',
        routeId: ROUTE_IDS.REPORTS,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORTS_VIEW,
        telemetryId: TELEMETRY_IDS.REPORTS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_REPORTS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.REPORTS_PLANNED,
        titleKey: I18N_KEYS.NAVIGATION_REPORTS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-report-detail',
        routeId: ROUTE_IDS.REPORT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.REPORTS,
        feature: FEATURE_KEYS.REPORT_DETAIL_READ,
        telemetryId: TELEMETRY_IDS.REPORT_DETAIL_READ,
        accessibilityLabelKey: I18N_KEYS.PAGE_REPORT_DETAIL_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_REPORT_DETAIL_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-reconciliation',
        routeId: ROUTE_IDS.RECONCILIATION,
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.RECONCILIATION_VIEW,
        telemetryId: TELEMETRY_IDS.RECONCILIATION_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_RECONCILIATION,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.RECONCILIATION_PLANNED,
        titleKey: I18N_KEYS.NAVIGATION_RECONCILIATION,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-corporate-actions',
        routeId: ROUTE_IDS.CORPORATE_ACTIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.CORPORATE_ACTIONS_VIEW,
        telemetryId: TELEMETRY_IDS.CORPORATE_ACTIONS_VIEW,
        accessibilityLabelKey: I18N_KEYS.NAVIGATION_CORPORATE_ACTIONS,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.CORPORATE_ACTIONS_PLANNED,
        titleKey: I18N_KEYS.NAVIGATION_CORPORATE_ACTIONS,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-forgot-password',
        routeId: ROUTE_IDS.FORGOT_PASSWORD,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_FORGOT_PASSWORD,
        telemetryId: TELEMETRY_IDS.AUTH_FORGOT_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.AUTH_FORGOT_PASSWORD,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_FORGOT_PASSWORD,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-reset-password',
        routeId: ROUTE_IDS.RESET_PASSWORD,
        schemaVersion: 1,
        module: MODULE_KEYS.AUTH,
        feature: FEATURE_KEYS.AUTH_RESET_PASSWORD,
        telemetryId: TELEMETRY_IDS.AUTH_RESET_PASSWORD,
        accessibilityLabelKey: I18N_KEYS.AUTH_RESET_PASSWORD,
        privacyClassification: 'PUBLIC',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.AUTH_RESET_PASSWORD,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-hub',
        routeId: ROUTE_IDS.ADMIN_HUB,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-admin-hub',
        routeId: ROUTE_IDS.PLATFORM_ADMIN_HUB,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-admin-hub',
        routeId: ROUTE_IDS.ORGANIZATION_ADMIN_HUB,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_ADMIN_ACCESSIBILITY,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.HUB_ADMIN_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-governance-workspace',
        routeId: ROUTE_IDS.ORGANIZATION_GOVERNANCE_WORKSPACE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION_GOVERNANCE,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION_GOVERNANCE,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_GOVERNANCE_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_GOVERNANCE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Data hub and included routes
    {
        id: 'page-data-hub',
        routeId: ROUTE_IDS.DATA_HUB,
        schemaVersion: 1,
        module: MODULE_KEYS.DATA,
        feature: FEATURE_KEYS.DATA_HUB,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.HUB_DATA_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.HUB_DATA_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-instruments',
        routeId: ROUTE_IDS.DATA_INSTRUMENTS,
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-instrument-detail',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-instrument-create',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-instrument-edit',
        routeId: ROUTE_IDS.DATA_INSTRUMENT_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.INSTRUMENTS,
        feature: FEATURE_KEYS.DATA_INSTRUMENTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_INSTRUMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-imports',
        routeId: ROUTE_IDS.DATA_IMPORTS,
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-import-upload',
        routeId: ROUTE_IDS.DATA_IMPORT_UPLOAD,
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORTS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-import-detail',
        routeId: ROUTE_IDS.DATA_IMPORT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.IMPORT,
        feature: FEATURE_KEYS.DATA_IMPORT_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_IMPORTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-reconciliation',
        routeId: ROUTE_IDS.DATA_RECONCILIATION,
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-reconciliation-detail',
        routeId: ROUTE_IDS.DATA_RECONCILIATION_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-reconciliation-create',
        routeId: ROUTE_IDS.DATA_RECONCILIATION_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.RECONCILIATION,
        feature: FEATURE_KEYS.DATA_RECONCILIATION,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_RECONCILIATION_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-corporate-actions',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-corporate-action-detail',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTION_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-corporate-action-create',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-corporate-action-edit',
        routeId: ROUTE_IDS.DATA_CORPORATE_ACTION_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.CORPORATE_ACTIONS,
        feature: FEATURE_KEYS.DATA_CORPORATE_ACTIONS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CORPORATE_ACTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-holdings',
        routeId: ROUTE_IDS.DATA_HOLDINGS,
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.DATA_HOLDINGS,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'REDIRECT',
        titleKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-holdings-detail',
        routeId: ROUTE_IDS.DATA_HOLDINGS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.HOLDINGS,
        feature: FEATURE_KEYS.DATA_HOLDINGS_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'REDIRECT',
        titleKey: I18N_KEYS.PAGE_HOLDINGS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-fact-batches',
        routeId: ROUTE_IDS.DATA_FACT_BATCHES,
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCHES,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-fact-batch-detail',
        routeId: ROUTE_IDS.DATA_FACT_BATCH_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCH_DETAIL,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-fact-batch-create',
        routeId: ROUTE_IDS.DATA_FACT_BATCH_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.FACTS,
        feature: FEATURE_KEYS.DATA_FACT_BATCHES,
        telemetryId: TELEMETRY_IDS.DATA_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FACT_BATCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-organizations-list',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-organizations-create',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-organizations-detail',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_DETAIL_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_DETAIL_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-organizations-edit',
        routeId: ROUTE_IDS.PLATFORM_ORGANIZATIONS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_ORGANIZATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-modules-list',
        routeId: ROUTE_IDS.PLATFORM_MODULES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-modules-detail',
        routeId: ROUTE_IDS.PLATFORM_MODULES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        titleKey: I18N_KEYS.PAGE_PLATFORM_MODULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-module-assignments',
        routeId: ROUTE_IDS.PLATFORM_MODULE_ASSIGNMENTS,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-users-list',
        routeId: ROUTE_IDS.PLATFORM_USERS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-users-create',
        routeId: ROUTE_IDS.PLATFORM_USERS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_CREATE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_CREATE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-users-detail',
        routeId: ROUTE_IDS.PLATFORM_USERS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-users-edit',
        routeId: ROUTE_IDS.PLATFORM_USERS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_USERS_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_USERS_EDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-health',
        routeId: ROUTE_IDS.PLATFORM_HEALTH,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_HUB,
        telemetryId: TELEMETRY_IDS.PLATFORM_HEALTH_CHECK,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_HEALTH_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_HEALTH_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-support-delegations-list',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-support-delegations-create',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-support-delegations-detail',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-support-delegations-revoke',
        routeId: ROUTE_IDS.PLATFORM_SUPPORT_DELEGATIONS_REVOKE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.CARD_MAINTENANCE_DELEGATIONS,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_SUPPORT_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-audit-list',
        routeId: ROUTE_IDS.PLATFORM_AUDIT_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-audit-detail',
        routeId: ROUTE_IDS.PLATFORM_AUDIT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PLATFORM_AUDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-branches-list',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-branches-create',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-branches-detail',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-branches-edit',
        routeId: ROUTE_IDS.ORGANIZATION_BRANCHES_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_BRANCHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-modules-list',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-modules-create',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-modules-configure',
        routeId: ROUTE_IDS.ORGANIZATION_MODULES_CONFIGURE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MODULE_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-users-list',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-users-create',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-users-detail',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-users-edit',
        routeId: ROUTE_IDS.ORGANIZATION_USERS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_USERS,
        telemetryId: TELEMETRY_IDS.ADMIN_USERS,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_USERS_EDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-roles-list',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-roles-create',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-roles-detail',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-roles-edit',
        routeId: ROUTE_IDS.ORGANIZATION_ROLES_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_EDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_ROLES_EDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-permission-assignments-list',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-permission-assignments-edit',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_ASSIGNMENTS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ROLES,
        telemetryId: TELEMETRY_IDS.ADMIN_ROLES,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'PLANNED',
        disabledReasonKey: I18N_KEYS.COMMON_PLANNED,
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_PERMISSION_ASSIGNMENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-clients-list',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-clients-create',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-clients-detail',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-clients-edit',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENTS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-client-accounts-list',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-client-accounts-create',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-client-accounts-detail',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-client-accounts-edit',
        routeId: ROUTE_IDS.ORGANIZATION_CLIENT_ACCOUNTS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_CLIENT_ACCOUNTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-portfolios-list',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-portfolios-create',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-portfolios-detail',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-portfolios-edit',
        routeId: ROUTE_IDS.ORGANIZATION_PORTFOLIOS_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_ORGANIZATION,
        telemetryId: TELEMETRY_IDS.ADMIN_ORGANIZATION,
        accessibilityLabelKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PORTFOLIOS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fiscal-years-list',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEARS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEARS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEARS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fiscal-year-create',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_CREATE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_CREATE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fiscal-year-detail',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_DETAIL_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_DETAIL_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fiscal-year-periods',
        routeId: ROUTE_IDS.ADMIN_FISCAL_YEAR_PERIODS,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.ADMIN_FISCAL_CALENDAR,
        telemetryId: TELEMETRY_IDS.CARD_FISCAL_CALENDAR,
        accessibilityLabelKey: I18N_KEYS.PAGE_FISCAL_YEAR_PERIODS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FISCAL_YEAR_PERIODS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-opening-cutover-list',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-opening-cutover-create',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-opening-cutover-detail',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-opening-cutover-edit',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.CARD_OPENING_CUTOVER,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-opening-cutover-history',
        routeId: ROUTE_IDS.ADMIN_OPENING_CUTOVER_HISTORY,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_OPENING_CUTOVER,
        telemetryId: TELEMETRY_IDS.HUB_ADMIN_SETUP_HISTORY,
        accessibilityLabelKey: I18N_KEYS.PAGE_OPENING_CUTOVER_HISTORY_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_OPENING_CUTOVER_HISTORY_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-tax-rules-list',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-tax-rules-create',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-tax-rules-detail',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-tax-rules-edit',
        routeId: ROUTE_IDS.ADMIN_TAX_RULES_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_TAX_RULES,
        telemetryId: TELEMETRY_IDS.CARD_TAX_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_TAX_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fee-rules-list',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fee-rules-create',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fee-rules-detail',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-admin-fee-rules-edit',
        routeId: ROUTE_IDS.ADMIN_FEE_RULES_EDIT,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_FEE_RULES,
        telemetryId: TELEMETRY_IDS.CARD_FEE_RULES,
        accessibilityLabelKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'ALWAYS',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_FEE_RULES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-audit-list',
        routeId: ROUTE_IDS.ORGANIZATION_AUDIT_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-organization-audit-detail',
        routeId: ROUTE_IDS.ORGANIZATION_AUDIT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_AUDIT,
        telemetryId: TELEMETRY_IDS.ADMIN_AUDIT,
        accessibilityLabelKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_ORGANIZATION_AUDIT_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // ── P2 hardening: new Data Hub PLANNED pages ──────────────────────────────
    // Market Data
    {
        id: 'page-data-market-prices',
        routeId: ROUTE_IDS.DATA_MARKET_PRICES,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_PRICES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-market-price-detail',
        routeId: ROUTE_IDS.DATA_MARKET_PRICE_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_PRICES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MARKET_PRICES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-fx-rates',
        routeId: ROUTE_IDS.DATA_FX_RATES,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_FX_RATES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_FX_RATES_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_FX_RATES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-benchmarks',
        routeId: ROUTE_IDS.DATA_BENCHMARKS,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_BENCHMARKS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_BENCHMARKS_TITLE,
        privacyClassification: 'CONFIDENTIAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_BENCHMARKS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-market-data-providers',
        routeId: ROUTE_IDS.DATA_MARKET_DATA_PROVIDERS,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_DATA_PROVIDERS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_DATA_PROVIDERS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MARKET_DATA_PROVIDERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-market-data-exceptions',
        routeId: ROUTE_IDS.DATA_MARKET_DATA_EXCEPTIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.MARKET_DATA,
        feature: FEATURE_KEYS.DATA_MARKET_DATA_EXCEPTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_MARKET,
        accessibilityLabelKey: I18N_KEYS.CARD_MARKET_DATA_EXCEPTIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MARKET_DATA_EXCEPTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Portfolio Management
    {
        id: 'page-data-clients',
        routeId: ROUTE_IDS.DATA_CLIENTS,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CLIENTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-client-detail',
        routeId: ROUTE_IDS.DATA_CLIENT_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CLIENTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CLIENTS_DATA_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-portfolios',
        routeId: ROUTE_IDS.DATA_PORTFOLIOS,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PORTFOLIOS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-portfolio-detail',
        routeId: ROUTE_IDS.DATA_PORTFOLIO_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PORTFOLIOS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_PORTFOLIOS_DATA_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-mandates',
        routeId: ROUTE_IDS.DATA_MANDATES,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_MANDATES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_MANDATES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MANDATES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-positions',
        routeId: ROUTE_IDS.DATA_POSITIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-positions-detail',
        routeId: ROUTE_IDS.DATA_POSITIONS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITIONS_DETAIL,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_POSITIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-cash',
        routeId: ROUTE_IDS.DATA_CASH,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CASH,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CASH_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CASH_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-cash-detail',
        routeId: ROUTE_IDS.DATA_CASH_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_CASH_DETAIL,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_CASH_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'ALWAYS',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CASH_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-performance',
        routeId: ROUTE_IDS.DATA_PERFORMANCE,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_PERFORMANCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_PORTFOLIO,
        accessibilityLabelKey: I18N_KEYS.CARD_PERFORMANCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_PERFORMANCE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Settlement and Custody
    {
        id: 'page-data-settlement-instructions',
        routeId: ROUTE_IDS.DATA_SETTLEMENT_INSTRUCTIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-position-transfers',
        routeId: ROUTE_IDS.DATA_POSITION_TRANSFERS,
        schemaVersion: 1,
        module: MODULE_KEYS.PORTFOLIO_MGMT,
        feature: FEATURE_KEYS.DATA_POSITION_TRANSFERS,
        telemetryId: TELEMETRY_IDS.CARD_POSITION_TRANSFERS,
        accessibilityLabelKey: I18N_KEYS.CARD_POSITION_TRANSFERS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_POSITION_TRANSFERS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-management-fees',
        routeId: ROUTE_IDS.DATA_MANAGEMENT_FEES,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_MANAGEMENT_FEES,
        telemetryId: TELEMETRY_IDS.CARD_MANAGEMENT_FEES,
        accessibilityLabelKey: I18N_KEYS.CARD_MANAGEMENT_FEES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'accounting',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_MANAGEMENT_FEES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-performance-fees',
        routeId: ROUTE_IDS.DATA_PERFORMANCE_FEES,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_PERFORMANCE_FEES,
        telemetryId: TELEMETRY_IDS.CARD_PERFORMANCE_FEES,
        accessibilityLabelKey: I18N_KEYS.CARD_PERFORMANCE_FEES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'accounting',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_PERFORMANCE_FEES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-custody-balances',
        routeId: ROUTE_IDS.DATA_CUSTODY_BALANCES,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_CUSTODY_BALANCES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_CUSTODY_BALANCES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CUSTODY_BALANCES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-settlement-failures',
        routeId: ROUTE_IDS.DATA_SETTLEMENT_FAILURES,
        schemaVersion: 1,
        module: MODULE_KEYS.SETTLEMENT,
        feature: FEATURE_KEYS.DATA_SETTLEMENT_INSTRUCTIONS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_SETTLEMENT,
        accessibilityLabelKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_SETTLEMENT_INSTRUCTIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Accounting
    {
        id: 'page-data-journals',
        routeId: ROUTE_IDS.DATA_JOURNALS,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-journal-detail',
        routeId: ROUTE_IDS.DATA_JOURNAL_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-journal-create',
        routeId: ROUTE_IDS.DATA_JOURNAL_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_JOURNALS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_JOURNALS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-chart-of-accounts',
        routeId: ROUTE_IDS.DATA_CHART_OF_ACCOUNTS,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_CHART_OF_ACCOUNTS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_CHART_OF_ACCOUNTS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_CHART_OF_ACCOUNTS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-fiscal-periods',
        routeId: ROUTE_IDS.DATA_FISCAL_PERIODS,
        schemaVersion: 1,
        module: MODULE_KEYS.ACCOUNTING,
        feature: FEATURE_KEYS.DATA_FISCAL_PERIODS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_ACCOUNTING,
        accessibilityLabelKey: I18N_KEYS.CARD_FISCAL_PERIODS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_FISCAL_PERIODS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Compliance and Risk
    {
        id: 'page-data-compliance-breaches',
        routeId: ROUTE_IDS.DATA_COMPLIANCE_BREACHES,
        schemaVersion: 1,
        module: MODULE_KEYS.COMPLIANCE,
        feature: FEATURE_KEYS.DATA_COMPLIANCE_BREACHES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-risk-exposures',
        routeId: ROUTE_IDS.DATA_RISK_EXPOSURES,
        schemaVersion: 1,
        module: MODULE_KEYS.RISK,
        feature: FEATURE_KEYS.DATA_RISK_EXPOSURES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_RISK_EXPOSURES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_RISK_EXPOSURES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-compliance-breach-detail',
        routeId: ROUTE_IDS.DATA_COMPLIANCE_BREACH_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.COMPLIANCE,
        feature: FEATURE_KEYS.DATA_COMPLIANCE_BREACHES,
        telemetryId: TELEMETRY_IDS.HUB_DATA_COMPLIANCE,
        accessibilityLabelKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_COMPLIANCE_BREACHES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Tax
    {
        id: 'page-data-tax-records',
        routeId: ROUTE_IDS.DATA_TAX_RECORDS,
        schemaVersion: 1,
        module: MODULE_KEYS.TAX,
        feature: FEATURE_KEYS.DATA_TAX_RECORDS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_TAX,
        accessibilityLabelKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-tax-calculations',
        routeId: ROUTE_IDS.DATA_TAX_CALCULATIONS,
        schemaVersion: 1,
        module: MODULE_KEYS.TAX,
        feature: FEATURE_KEYS.DATA_TAX_RECORDS,
        telemetryId: TELEMETRY_IDS.HUB_DATA_TAX,
        accessibilityLabelKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_TAX_RECORDS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Evidence
    {
        id: 'page-data-evidence',
        routeId: ROUTE_IDS.DATA_EVIDENCE,
        schemaVersion: 1,
        module: MODULE_KEYS.DOCUMENTS,
        feature: FEATURE_KEYS.DATA_EVIDENCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_DOCUMENTS,
        accessibilityLabelKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-data-evidence-detail',
        routeId: ROUTE_IDS.DATA_EVIDENCE_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.DOCUMENTS,
        feature: FEATURE_KEYS.DATA_EVIDENCE,
        telemetryId: TELEMETRY_IDS.HUB_DATA_DOCUMENTS,
        accessibilityLabelKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        privacyClassification: 'FINANCIAL',
        auditPolicy: 'NONE',
        owner: 'investment',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.CARD_EVIDENCE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // ── P2 hardening: new Admin Hub PLANNED pages ─────────────────────────────
    // Organization admin — missing capabilities
    {
        id: 'page-org-approval-policies',
        routeId: ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_APPROVAL_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-approval-policies-detail',
        routeId: ROUTE_IDS.ORGANIZATION_APPROVAL_POLICIES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_APPROVAL_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_APPROVAL_POLICIES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-permission-bundles',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PERMISSION_BUNDLES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-permission-bundles-detail',
        routeId: ROUTE_IDS.ORGANIZATION_PERMISSION_BUNDLES_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_PERMISSION_BUNDLES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_PERMISSION_BUNDLES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-retention-policies',
        routeId: ROUTE_IDS.ORGANIZATION_RETENTION_POLICIES,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_RETENTION_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_RETENTION_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_RETENTION_POLICIES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-export-policies',
        routeId: ROUTE_IDS.ORGANIZATION_EXPORT_POLICIES,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_EXPORT_POLICIES,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_EXPORT_POLICIES_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_EXPORT_POLICIES_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-security-settings',
        routeId: ROUTE_IDS.ORGANIZATION_SECURITY_SETTINGS,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SECURITY_SETTINGS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SECURITY_SETTINGS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_SECURITY_SETTINGS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-regulatory-profile',
        routeId: ROUTE_IDS.ORGANIZATION_REGULATORY_PROFILE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_REGULATORY_PROFILE,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_REGULATORY_PROFILE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_REGULATORY_PROFILE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-jurisdiction-settings',
        routeId: ROUTE_IDS.ORGANIZATION_JURISDICTION_SETTINGS,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_JURISDICTION_SETTINGS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_JURISDICTION_SETTINGS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_JURISDICTION_SETTINGS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-org-support-access',
        routeId: ROUTE_IDS.ORGANIZATION_SUPPORT_ACCESS,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SUPPORT_ACCESS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SUPPORT_ACCESS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_SUPPORT_ACCESS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    // Platform admin — missing capabilities
    {
        id: 'page-platform-security-posture',
        routeId: ROUTE_IDS.PLATFORM_SECURITY_POSTURE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_SECURITY_POSTURE,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_SECURITY_POSTURE_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_SECURITY_POSTURE_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-data-provider-health',
        routeId: ROUTE_IDS.PLATFORM_DATA_PROVIDER_HEALTH,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_DATA_PROVIDER_HEALTH,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_DATA_PROVIDER_HEALTH_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_DATA_PROVIDER_HEALTH_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-maintenance-delegations',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_LIST,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-maintenance-delegations-create',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_CREATE,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
    {
        id: 'page-platform-maintenance-delegations-detail',
        routeId: ROUTE_IDS.PLATFORM_MAINTENANCE_DELEGATIONS_DETAIL,
        schemaVersion: 1,
        module: MODULE_KEYS.ADMIN,
        feature: FEATURE_KEYS.ADMIN_MAINTENANCE_DELEGATIONS,
        telemetryId: TELEMETRY_IDS.ADMIN_HUB,
        accessibilityLabelKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        privacyClassification: 'INTERNAL',
        auditPolicy: 'NONE',
        owner: 'platform',
        availability: 'AVAILABLE',
        titleKey: I18N_KEYS.PAGE_MAINTENANCE_DELEGATIONS_TITLE,
        layout: 'single',
        states: baseStates,
        sections: [],
    },
];
export function getPageById(pageId) {
    return canonicalPageRegistry.find((p) => p.id === pageId);
}
export function getPageByRouteId(routeId) {
    return canonicalPageRegistry.find((p) => p.routeId === routeId);
}
export function getPageIds() {
    return canonicalPageRegistry.map((p) => p.id);
}
export function getRouteIdsWithPages() {
    return canonicalPageRegistry.map((p) => p.routeId);
}
